/**
 * Shared popover utilities for overlay-style controls (qds-select, qds-date-picker).
 *
 * Design rules (from the specs):
 * - Rendered inside the component's shadow root, NOT portalled to <body>.
 *   Portalling breaks style encapsulation and every existing QDS overlay
 *   stays in-root.
 * - position: absolute against the trigger.
 * - Flip to top when the space below is less than the popover height and the
 *   space above is greater. Measure on open, not on every scroll.
 * - Only one popover open at a time: dispatch a `qds-popover-open` event on
 *   document and close on receiving one from another instance.
 * - Close on: Escape, outside click, trigger click (toggle).
 */

export type PopoverPlacement = 'bottom' | 'top';

export interface PopoverMeasurements {
  triggerRect: DOMRect;
  popoverHeight: number;
  spaceBelow: number;
  spaceAbove: number;
}

/**
 * Determines whether the popover should open below or above the trigger.
 * Falls back to 'bottom' unless there's clearly more room above.
 */
export function computePlacement(
  triggerRect: DOMRect,
  popoverHeight: number,
  viewportHeight: number = typeof window !== 'undefined' ? window.innerHeight : 800,
): PopoverPlacement {
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  if (spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
    return 'top';
  }
  return 'bottom';
}

/**
 * Manages a popover's open/close lifecycle, positioning, dismissal, and
 * single-open-at-a-time coordination. Designed to be instantiated per
 * component instance.
 */
export class PopoverController {
  private _host: HTMLElement;
  private _trigger: HTMLElement;
  private _panel: HTMLElement;
  private _placement: PopoverPlacement = 'bottom';
  private _open = false;
  private _onOutsideClick: (e: Event) => void;
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onPopoverOpen: (e: Event) => void;
  private _cleanup: (() => void) | null = null;

  constructor(host: HTMLElement, trigger: HTMLElement, panel: HTMLElement) {
    this._host = host;
    this._trigger = trigger;
    this._panel = panel;

    this._onOutsideClick = (e: Event): void => {
      if (!this._open) return;
      const path = e.composedPath();
      if (!path.includes(this._host)) {
        this.hide();
      }
    };

    this._onKeyDown = (e: KeyboardEvent): void => {
      if (!this._open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
        this._trigger.focus();
      }
    };

    this._onPopoverOpen = (e: Event): void => {
      const ce = e as CustomEvent;
      if (ce.detail && ce.detail.host && ce.detail.host !== this._host) {
        if (this._open) this.hide();
      }
    };
  }

  get open(): boolean {
    return this._open;
  }

  get placement(): PopoverPlacement {
    return this._placement;
  }

  /** Opens the popover, positions it, and registers dismissal listeners. */
  show(): void {
    if (this._open) return;
    this._open = true;

    // Single-open-at-a-time: tell other instances to close.
    document.dispatchEvent(new CustomEvent('qds-popover-open', {
      bubbles: true,
      composed: true,
      detail: { host: this._host },
    }));

    // Position
    this._position();

    // Listeners
    document.addEventListener('click', this._onOutsideClick, true);
    document.addEventListener('keydown', this._onKeyDown, true);
    document.addEventListener('qds-popover-open', this._onPopoverOpen);

    this._cleanup = (): void => {
      document.removeEventListener('click', this._onOutsideClick, true);
      document.removeEventListener('keydown', this._onKeyDown, true);
      document.removeEventListener('qds-popover-open', this._onPopoverOpen);
    };
  }

  /** Closes the popover and removes all listeners. */
  hide(): void {
    if (!this._open) return;
    this._open = false;
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = null;
    }
  }

  /** Toggles open/closed. */
  toggle(): void {
    if (this._open) {
      this.hide();
    } else {
      this.show();
    }
  }

  /** Re-positions the popover (call on scroll or resize if needed). */
  reposition(): void {
    if (this._open) {
      this._position();
    }
  }

  /** Removes all listeners — call in disconnectedCallback. */
  destroy(): void {
    this.hide();
  }

  private _position(): void {
    const triggerRect = this._trigger.getBoundingClientRect();
    const popoverHeight = this._panel.offsetHeight || 320;
    this._placement = computePlacement(triggerRect, popoverHeight);

    if (this._placement === 'top') {
      this._panel.style.bottom = `${this._trigger.offsetHeight}px`;
      this._panel.style.top = '';
    } else {
      this._panel.style.top = `${this._trigger.offsetHeight}px`;
      this._panel.style.bottom = '';
    }
    this._panel.style.position = 'absolute';
    this._panel.style.left = '0';
    this._panel.style.zIndex = 'var(--qds-popover-z, 1000)';
  }
}
